import { Mail, MapPin, Phone } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { PageHero, Reveal } from "@/components/site/Reveal";
import { LeadForm } from "@/components/site/LeadForm";
import { CallDirections, MapEmbed } from "@/components/site/LocationCard";
import { OpenStatus } from "@/components/site/OpenStatus";
import { dayRange } from "@/lib/hours";
import { fullAddress } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

function HoursMatrix({ locations }) {
  return (
    <div className="overflow-x-auto rounded-md border border-white/10" data-testid="contact-hours-table">
      <table className="w-full min-w-[420px] text-sm">
        <thead>
          <tr className="border-b border-white/10 bg-white/[0.03]">
            <th className="p-3 text-left font-mono-ui text-[11px] uppercase tracking-wider text-white/50">Day</th>
            {locations.map((l) => <th key={l.id} className="p-3 text-right font-mono-ui text-[11px] uppercase tracking-wider text-[var(--gold)]">{l.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {locations[0].hours.map((h, i) => (
            <tr key={h.day} className="border-b border-white/5">
              <td className="p-3 font-display text-base font-bold uppercase text-white">{h.day}</td>
              {locations.map((l) => <td key={l.id} className="p-3 text-right font-mono-ui text-xs text-white/75">{dayRange(l.hours[i])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Contact() {
  const { content } = useSite();
  usePageMeta("Contact Iron Pump Gym | Bayside & Flushing Queens Gym Hours", "Contact Iron Pump Gym in Bayside and Flushing, Queens. Addresses, phone numbers, maps and hours of operation.");
  return (
    <>
      <PageHero eyebrow="Contact" title="Talk to us" sub="Questions about memberships, guest passes or anything else? Send a message or call either gym." image="/images/ropes.webp" />
      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-8 lg:grid-cols-[1.1fr_1fr]">
        <Reveal>
          <div className="rounded-md border border-white/10 bg-[#121212] p-6 sm:p-8" data-testid="contact-form-card">
            <h2 className="h-display text-4xl text-white">Send a message</h2>
            <p className="mb-6 mt-2 text-sm text-white/55">We usually reply within one business day.</p>
            <LeadForm defaultType="contact" idPrefix="contact" />
          </div>
        </Reveal>
        <div className="space-y-6">
          <a href={`mailto:${content.settings.email}`} className="flex items-center gap-3 text-white hover:text-[var(--gold)]" data-testid="contact-email-link"><Mail className="h-5 w-5 text-[var(--gold)]" /> {content.settings.email}</a>
          <h2 className="h-display text-3xl text-white">Hours of operation</h2>
          <HoursMatrix locations={content.locations} />
        </div>
      </section>
      <section className="mx-auto grid max-w-7xl gap-8 px-4 pb-24 sm:px-8 lg:grid-cols-2">
        {content.locations.map((loc, i) => (
          <Reveal key={loc.id} delay={i * 0.1}>
            <div className="space-y-4 rounded-md border border-white/10 bg-[#121212] p-6" data-testid={`contact-location-${loc.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="h-display text-4xl text-white">{loc.name}</h3>
                <OpenStatus loc={loc} />
              </div>
              <p className="flex gap-2 text-white/85"><MapPin className="mt-1 h-4 w-4 shrink-0 text-[var(--gold)]" />{fullAddress(loc)}{loc.note && ` (${loc.note})`}</p>
              <a href={`tel:${loc.phone_tel}`} className="flex items-center gap-2 text-white/85 hover:text-[var(--gold)]"><Phone className="h-4 w-4 text-[var(--gold)]" />{loc.phone}</a>
              <MapEmbed loc={loc} className="h-56" />
              <CallDirections loc={loc} />
            </div>
          </Reveal>
        ))}
      </section>
    </>
  );
}
